import { CATEGORIAS_CONEXAO } from 'utils/direcionadores';
import { normalizeCategoryPremio } from 'utils/iniciativa-categoria';
import type { CategoriaPreInscricao } from 'utils/pessoa';

/** O mínimo de uma iniciativa gravada para saber que vaga ela ocupa. */
export interface IniciativaOcupante {
  _id?: string;
  owner?: string;
  active?: boolean;
  categoryPremio?: string;
  categoria_conexao?: string;
}

export interface VagasPreInscricao {
  /** Categorias em que a pessoa ainda pode cadastrar, na ordem de `categoriasPermitidas`. */
  categorias: CategoriaPreInscricao[];
  /** Subcategorias do Conexão ainda livres (ids de CATEGORIAS_CONEXAO). */
  subcategoriasConexao: string[];
}

/** Público do Prêmio BB: quem é dos dois escolhe um deles e fica só nele. */
const PUBLICOS_EXCLUSIVOS: CategoriaPreInscricao[] = ['CONEXAO', 'PILAR'];

/**
 * Vagas de pré-inscrição da pessoa (regra 2027):
 * - Conexão: 1 iniciativa por subcategoria (Negócios e Resultados, Atendimento e Exp. do Cliente);
 * - Pilar e ELBB: 1 iniciativa;
 * - quem é Conexão e Predefinido fica no público da primeira iniciativa ativa.
 *
 * `iniciativaEditadaId` libera a vaga da própria iniciativa na edição. Excluir
 * (active: false) libera a vaga e, se era a única, a escolha do público.
 * As triggers `validaVagaPreInscricaoCriar`/`Editar` da bb_task_pre__c aplicam a mesma regra no servidor.
 */
export function getVagasPreInscricao(
  categoriasPermitidas: CategoriaPreInscricao[],
  iniciativas: IniciativaOcupante[],
  ownerId: string | undefined,
  iniciativaEditadaId?: string
): VagasPreInscricao {
  const ocupadas = iniciativas.filter(
    (i) => i.owner === ownerId && i.active !== false && (!iniciativaEditadaId || i._id !== iniciativaEditadaId)
  );
  const categoriasOcupadas = ocupadas.map((i) => normalizeCategoryPremio(i.categoryPremio));

  const publicoEscolhido = PUBLICOS_EXCLUSIVOS.find((publico) => categoriasOcupadas.includes(publico));
  const subcategoriasConexao = CATEGORIAS_CONEXAO.map((c) => c.id).filter(
    (id) => !ocupadas.some((i) => normalizeCategoryPremio(i.categoryPremio) === 'CONEXAO' && i.categoria_conexao === id)
  );

  const temVaga = (categoria: CategoriaPreInscricao) => {
    if (publicoEscolhido && PUBLICOS_EXCLUSIVOS.includes(categoria) && categoria !== publicoEscolhido) return false;
    if (categoria === 'CONEXAO') return subcategoriasConexao.length > 0;
    return !categoriasOcupadas.includes(categoria);
  };

  return { categorias: categoriasPermitidas.filter(temVaga), subcategoriasConexao };
}
